import instance, { isAxiosErrorHandled } from 'api';
import { Modal } from 'components/modal/Modal';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, selectModalState, ModalTypes } from 'reducers/modal';
import Button from 'components/button/Button';
import { AxiosRequestConfig } from 'axios';
import { ERROR_CODES, getErrorMessage } from 'errors';
import { endpoints } from 'endpoints.config';

import UploadIcon from 'assets/ibanera/Icon_Upload.png';
import RedX from 'assets/ibanera/Icon_RedCross.png';
import { useResetableDropzone } from 'helpers/useResetableDropzoneBusinessForm';
import { roundToPrecision } from 'helpers/calculateToPrecision';
import { Form, Formik, FormikHelpers } from 'formik';
import FormTextField from 'components/form/FormTextField';
import { DocumentFieldValue } from '../businessVerification/components/BusinessVerificationDocuments';
import { Toast, ToastMessageReason } from '../../helpers/toast';
import { UploadDocumentFlowType } from './UploadDocumentModal';
import { FileError } from 'react-dropzone';

type FormValues = {
    note: string;
};
const initialValues: FormValues = {
    note: '',
};
export const UploadMulitpleDocumentModalBusinessForm = () => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState('');
    const modalState = useSelector(selectModalState);

    let fieldName = '';
    let values = null;
    if (
        modalState.modalType === ModalTypes.UPLOAD_MULTI_BUSINESS_DOCUMENT &&
        modalState.data.fieldName &&
        modalState.data.values
    ) {
        fieldName = modalState.data.fieldName;
        values = modalState.data.values;
    }

    const additionalDocs = ((values?.documentsUpload ?? []) as DocumentFieldValue[]).filter(
        (doc) => doc.bIsAdditionalDocument
    );

    const maxSize = 10 * 1024 * 1024;
    const { acceptedFiles, getRootProps, getInputProps, resetFiles } = useResetableDropzone(
        {
            multiple: false,
            accept: 'application/pdf',
            maxSize: maxSize,
            validator: (file) => {
                // https://stackoverflow.com/questions/20690499/concrete-javascript-regular-expression-for-accented-characters-diacritics
                var validFilename =
                    /^[ a-zA-Z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F0-9_.@()-]+\.[^.]+$/i.test(
                        file.name
                    );
                if (!validFilename)
                    return {
                        message: `Invalid file name`,
                    } as FileError;

                if (file.name.length > 200)
                    return {
                        message: `Filename too long. Cannot be greater than 200 characters`,
                    } as FileError;

                if (file.size > maxSize) {
                    return {
                        message: `File size must be less than 10MB`,
                    } as FileError;
                }

                return null;
            },
            onDropRejected: (rejections) => {
                if (
                    rejections.some((reject) =>
                        reject.errors.some((error) => error.code === 'file-invalid-type')
                    )
                ) {
                    setErrorMessage('File type must be .pdf');
                } else if (
                    rejections.some((reject) =>
                        reject.errors.some((error) => error.code === 'file-too-large')
                    )
                ) {
                    setErrorMessage('File size must be less than 10MB');
                } else if (
                    rejections.length > 0 &&
                    rejections[0].errors.length > 0 &&
                    rejections[0].errors[0].message
                ) {
                    setErrorMessage(rejections[0].errors[0].message);
                } else setErrorMessage('File rejected - please upload a valid .pdf file');
            },
        },
        fieldName,
        values,
        setErrorMessage
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => setErrorMessage(''), [acceptedFiles]); //reset error message when files change

    if (modalState.modalType !== ModalTypes.UPLOAD_MULTI_BUSINESS_DOCUMENT) return null;

    const handleSubmit = (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        if (!acceptedFiles || acceptedFiles.length < 1)
            return setErrorMessage('Please upload a file');
        setIsSubmitting(true);
        setErrorMessage('');
        const formData = new FormData();
        formData.append('documentId', modalState.data.documentId.toString());
        formData.append('file', acceptedFiles[0]);
        formData.append('note', values.note);

        const config: AxiosRequestConfig = {
            headers: { 'content-type': 'multipart/form-data' },
        };

        instance
            .post(
                modalState.data.flowType === UploadDocumentFlowType.BusinessForm
                    ? endpoints.businessModule.uploadDocument
                    : modalState.data.flowType === UploadDocumentFlowType.BusinessFormV2
                    ? endpoints.businessVerificationV2Module.uploadDocument
                    : endpoints.businessVerificationV3Module.uploadDocument,
                formData,
                config
            )
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.status === '1') {
                        modalState.data.reloadTable();
                        setIsSubmitting(false);
                        modalState.data?.helpers?.setValue(acceptedFiles[0].name);
                        dispatch(closeModal());
                    }
                }
            })
            .catch((err) => {
                let translatedErrorMessage = getErrorMessage('Generic');
                // Generic error

                if (isAxiosErrorHandled(err) && err.response.data.errors) {
                    err.response.data.errors.forEach((error) => {
                        if (error.messageCode in ERROR_CODES)
                            translatedErrorMessage = getErrorMessage(error.messageCode);
                    });
                }
                if (isAxiosErrorHandled(err) && err.response.status === 413) {
                    translatedErrorMessage = 'File too large';
                }

                setErrorMessage(translatedErrorMessage);
                setIsSubmitting(false);
            });
    };

    const handleRemoveFile = (documentId: number, customersBusinessDocumentsId: number) => {
        setIsSubmitting(true);
        setErrorMessage('');
        const formData = new FormData();
        formData.append('documentId', documentId + '');
        customersBusinessDocumentsId &&
            formData.append('customersBusinessDocumentsId', customersBusinessDocumentsId + '');

        const config: AxiosRequestConfig = {
            headers: { 'content-type': 'multipart/form-data' },
        };

        instance
            .post(
                modalState.data.flowType === UploadDocumentFlowType.BusinessForm
                    ? endpoints.businessModule.uploadDocument
                    : modalState.data.flowType === UploadDocumentFlowType.BusinessFormV2
                    ? endpoints.businessVerificationV2Module.uploadDocument
                    : endpoints.businessVerificationV3Module.uploadDocument,
                formData,
                config
            )
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.status === '1') {
                        modalState.data.reloadTable();
                        setIsSubmitting(false);
                        modalState.data?.onRemovedItem(documentId, customersBusinessDocumentsId);
                        Toast.openToastMessage('Deleted file', ToastMessageReason.VALID);
                    }
                }
            })
            .catch(() => {
                Toast.openToastMessage('Failed to delete file', ToastMessageReason.ERROR);
                setIsSubmitting(false);
            });
    };

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            <Form>
                <Modal
                    backgroundClassName="DocumentUploadModal"
                    title={`Upload Other Documents`}
                    customButtons={
                        <div className="ModalNavigation">
                            <Button
                                priority="secondary"
                                type="button"
                                onClick={() => {
                                    dispatch(closeModal());
                                }}
                            >
                                Cancel
                            </Button>
                            <Button priority="primary" type="submit" disabled={isSubmitting}>
                                Upload
                            </Button>
                        </div>
                    }
                >
                    <div className="UploadDocumentModal">
                        {additionalDocs.length > 0 && (
                            <div className="UploadedFiles">
                                <h3>Uploaded Files</h3>
                                <div className="UploadedFilesWrapepr">
                                    {additionalDocs
                                        .filter((doc) => !!doc.customersBusinessDocumentsId)
                                        .map((doc, i) => (
                                            <DisplayFile
                                                key={i}
                                                doc={doc}
                                                handleRemoveFile={handleRemoveFile}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}

                        <p>Please upload your file below</p>
                        <div {...getRootProps({ className: 'Dropzone' })}>
                            <input {...getInputProps()} />
                            {acceptedFiles[0] ? (
                                <span>
                                    {acceptedFiles[0].name} -{' '}
                                    {roundToPrecision(acceptedFiles[0].size / 1000, 2)} kB
                                </span>
                            ) : (
                                <>
                                    <img src={UploadIcon} alt="upload" />
                                    <h5>PDF file</h5>
                                    <span>Click or drag to upload</span>
                                </>
                            )}
                        </div>
                        <div className="DropzoneFooter">
                            <span>Accepts - .pdf</span>
                            {acceptedFiles.length > 0 && (
                                <Button
                                    priority="secondary"
                                    className="ResetBtn"
                                    onClick={resetFiles}
                                >
                                    Reset
                                </Button>
                            )}
                        </div>
                        <FormTextField
                            field={'note'}
                            label={'Notes'}
                            type={'textarea'}
                            maxLength={500}
                            required={false}
                            inputProps={{ rows: 3, style: { resize: 'vertical' } }}
                        ></FormTextField>
                        {errorMessage && <p className="ErrorText NoMargin">{errorMessage}</p>}
                    </div>
                </Modal>
            </Form>
        </Formik>
    );
};

type DisplayFileProps = {
    doc: DocumentFieldValue;
    handleRemoveFile: (documentId: number, customersBusinessDocumentsId: number) => void;
};

const DisplayFile: React.FC<DisplayFileProps> = ({ doc, handleRemoveFile }) => (
    <div className="UploadedFile">
        <div>{doc.fileName ?? 'No name'}</div>
        <button
            aria-label={`Delete file ${doc.fileName}`}
            type="button"
            onClick={() => handleRemoveFile(doc.documentTypesID, doc.customersBusinessDocumentsId)}
        >
            <img src={RedX} alt="Delete file" />
        </button>
    </div>
);
